import { getSessionEnvironmentConditionId } from '../core';
import type { ModuleId, ModuleSummary, SessionRecord } from '../core/types';

export interface TrainingScorePoint {
  sessionId: string;
  date: string;
  value: number;
  environmentConditionId: string;
  protocolVersion: string;
}

const THRESHOLD_RANGES: Readonly<Record<ModuleId, {
  minimum: number;
  maximum: number;
  direction: 'higher' | 'lower';
}>> = {
  'read-switch': { minimum: 120, maximum: 347, direction: 'lower' },
  'central-peripheral': { minimum: 363, maximum: 725, direction: 'lower' },
  'player-track': { minimum: 7.4, maximum: 12.4, direction: 'higher' },
  'moving-target': { minimum: 10, maximum: 22, direction: 'lower' },
};

function clampScore(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Produces a 0–100 task score. Accuracy contributes 30%; when a stable adaptive
 * threshold is available, completed difficulty contributes the remaining 70%.
 */
export function moduleTrainingScore(summary: ModuleSummary): number | undefined {
  if (summary.validTrials <= 0 || !Number.isFinite(summary.accuracy)) return undefined;
  const accuracyScore = clampScore(summary.accuracy * 100);
  if (summary.threshold === undefined || !Number.isFinite(summary.threshold)) {
    return Number(accuracyScore.toFixed(1));
  }
  const range = THRESHOLD_RANGES[summary.moduleId];
  const progress = (summary.threshold - range.minimum) / (range.maximum - range.minimum);
  const thresholdScore = clampScore((range.direction === 'higher' ? progress : 1 - progress) * 100);
  return Number((thresholdScore * 0.7 + accuracyScore * 0.3).toFixed(1));
}

function scorePoint(
  session: SessionRecord,
  value: number,
): TrainingScorePoint {
  return {
    sessionId: session.id,
    date: session.completedAt ?? session.startedAt,
    value,
    environmentConditionId: getSessionEnvironmentConditionId(session),
    protocolVersion: session.protocolVersion,
  };
}

function completedSessionsAscending(sessions: readonly SessionRecord[]): SessionRecord[] {
  return sessions
    .filter((session) => session.status === 'completed')
    .sort((left, right) => left.startedAt.localeCompare(right.startedAt));
}

export function buildModuleScorePoints(
  moduleId: ModuleId,
  sessions: readonly SessionRecord[],
  summariesBySession: Readonly<Record<string, ModuleSummary[]>>,
): TrainingScorePoint[] {
  return completedSessionsAscending(sessions).flatMap((session) => {
    const summary = summariesBySession[session.id]?.find((item) => item.moduleId === moduleId);
    const value = summary ? moduleTrainingScore(summary) : undefined;
    return value === undefined ? [] : [scorePoint(session, value)];
  });
}

/**
 * Overall score is the mean of the most recent available score for each task
 * under the same protocol and environment. A single-task session updates only
 * that task, so the composite remains useful without bridging unlike conditions.
 */
export function buildOverallScorePoints(
  sessions: readonly SessionRecord[],
  summariesBySession: Readonly<Record<string, ModuleSummary[]>>,
): TrainingScorePoint[] {
  const latestByCondition = new Map<string, Map<ModuleId, number>>();
  const points: TrainingScorePoint[] = [];
  for (const session of completedSessionsAscending(sessions)) {
    const conditionKey = `${session.protocolVersion}:${getSessionEnvironmentConditionId(session)}`;
    const latest = latestByCondition.get(conditionKey) ?? new Map<ModuleId, number>();
    latestByCondition.set(conditionKey, latest);
    let updated = false;
    for (const summary of summariesBySession[session.id] ?? []) {
      const value = moduleTrainingScore(summary);
      if (value === undefined) continue;
      latest.set(summary.moduleId, value);
      updated = true;
    }
    if (!updated || latest.size === 0) continue;
    const value = [...latest.values()].reduce((total, score) => total + score, 0) / latest.size;
    points.push(scorePoint(session, Number(value.toFixed(1))));
  }
  return points;
}

export function filterScorePointsByDate(
  points: readonly TrainingScorePoint[],
  from: string,
  to: string,
): TrainingScorePoint[] {
  const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;
  return points.filter((point) => {
    const time = new Date(point.date).getTime();
    return time >= fromMs && time <= toMs;
  });
}
